
import {
  defineComponent,
  computed,
  onMounted,
  SetupContext,
  toRefs,
  PropType
} from "vue";
import {
  MypageFoodsRanking,
  MypageFoodsRankingItem,
  MypageUserRanking,
  MypageHistoryRecipes,
  MypageInquiryStatus
} from "@/types/mypage";
import NativeClient from "@/client/native-client";
import router from "@/router";
import store from "@/store";
  
export default defineComponent({
  name: "MypageReports",
  props: {
    /** 問診状態 0：不明、1：問診未回答、2：問診回答済 */
    inquiryStatus: {
       type: MypageInquiryStatus,
       default: null
    },
    /** 使い切り食品リスト */
    usedFoodList: {
      type: MypageFoodsRanking,
      default: null
    },
    /** ユーザー・ランキング */
    userRanking: {
      type: MypageUserRanking,
      default: null
    },
    /** 参照されたレシピ */
    historyRecipeList: {
      type: MypageHistoryRecipes,
      default: null
    }
  },
  setup(props, context: SetupContext) {
    // 定数
    const numberOfHistoryRecipeImages = 6;    // 表示するレシピ閲覧履歴の画像数

    // 文言
    const inquiryNotAnsweredText = "あなたのことを教えてください。食材やレシピの提案に反映します。";
    const inquiryAnsweredText =  "問診に回答済みです。回答結果をおすすめに反映します。";
    const usedFoodsTitleText = "食材使い切り";
    const noExistUsedFoodListText = "前月の使い切った食材が\n表示されます";
    const userRankingtitleText = "食材使い切り度";
    const noUserRankingText = "前月の使い切った食材に応じて\nランキングが表示されます";
    const preRankingValueText = "全体を100人とした場合";
    const postRankingValueText = "番目"
    const historyRecipeTitleText = "最近見たレシピ";
    const noHistoryMessageText = "過去一週間で閲覧したレシピはありません。";

    // 領域の有効無効
    const isValidInquiry = computed(() => {
      // 問診は、2023/8のリリースのため表示しない
      // if (props.inquiryStatus == null) {
      //   return false;
      // }
      // return true;
      return false;
    });
    const isValidUsedFoods = computed(() => {
      NativeClient.sendDebugLog("MypageReports isValidUsedFoods usedFoodList = " + JSON.stringify(props.usedFoodList));
      if (props.usedFoodList == null) {
        return false;
      }
      return true;
    });
    const isValidHistoryRecipe = computed(() => {
      if (props.historyRecipeList == null) {
        return false;
      }
      return true;
    });
    const isExistUsedFoodList = computed(() => {
      if (props.usedFoodList != null && props.usedFoodList.foods.length > 0) {
        return true;
      }
      return false;
    });
    const isNoRankingPreviousMonth = computed(() => {
      if (props.userRanking != null) {
        if (props.usedFoodList == null || props.usedFoodList.foods.length == 0) {
          return true;
        } else if (props.userRanking.rank == null || props.userRanking.rank == 100) {
          return true;
        } else {
          return false;
        }
      }
      return true;
    });
    const isNoHistory = computed(() => {
      if (props.historyRecipeList != null) {
        if (props.historyRecipeList.recipes.length == 0) {
          return true;
        } else {
          return false;
        }
      }
      return true;
    });
    
    const inquiryDescriptionText = computed(() => {
      let retString = "";
      if (props.inquiryStatus != null) {
        if (props.inquiryStatus.answered == null) {
          retString = inquiryNotAnsweredText;
        } else if (props.inquiryStatus.answered) {
          retString = inquiryAnsweredText;
        } else {
          retString = inquiryNotAnsweredText;
        }
      }
      return retString;
    });

    const rankingValueText = computed(() => {
      if (props.userRanking != null && props.userRanking.rank != null) {
        return `${ props.userRanking.rank }`;
      }
      return "0";
    });

    const historyRecipeDescriptionText = computed(() => {
      NativeClient.sendDebugLog("MypageReports historyRecipeDescriptionText historyRecipeList = " + JSON.stringify(props.historyRecipeList ));
      let count = 0;
      if (props.historyRecipeList != null) {
        count = props.historyRecipeList.totalCount;
      }
      return `あなたは過去一週間で${count}種類のレシピを閲覧しました。`;
    });

    // 使い切り食品
    const displayUsedFoodList = computed(() => {
      const displayUsedFoodList: Array<MypageFoodsRankingItem> = [];
      if (props.usedFoodList != null) {
        let count = 0;
        for (const element of props.usedFoodList.foods) {
          displayUsedFoodList.push(element);
          count ++;
          if (count >= 3) {
            break;
          }
        }
      }
      return displayUsedFoodList;
    });
    const numberOfUsedFoodsText = computed(() => {
      let count = 0;
      if (props.usedFoodList != null) {
        count = props.usedFoodList.foods.length;
      }
      return `あなたは先月、${count}種類の食材を使い切りました。`;
    });
    const usedFoodListItemImageSize = computed(() => {
      const screenWidth = window.innerWidth;
      const areaWidth = (screenWidth - 80) / 10;
      const imageWidth = (areaWidth - 2 * 2.5) / 3;
      NativeClient.sendDebugLog("MypageReports historyRecipeImageSize imageWidth = " + imageWidth);
      return {
        width: `${imageWidth}rem`,
        height: `${imageWidth}rem`
      }
    });

    // 閲覧履歴のレシピ画像
    const recipeImageList = computed(() => {
      if (props.historyRecipeList == null) {
        return [];
      }
      const recipeImageList: Array<string> = [];
      let count = 0;
      for (const recipe of props.historyRecipeList.recipes) {
        if (recipe.images.length > 0 && recipe.images[0].main != null) {
          recipeImageList.push(recipe.images[0].main);
        } else {
          recipeImageList.push("");
        }
        count ++;
        if (count >= numberOfHistoryRecipeImages) {
          break;
        }
      }
      return recipeImageList;
    });

    // 画像読み込みエラー発生
    const onImageError = (event: Event) =>{
      NativeClient.sendDebugLog("CommonHorizontalRecipeListItem onImageError");
      if (event.target) {
        NativeClient.sendDebugLog("CommonHorizontalRecipeListItem onImageError target exist");
        (event.target as HTMLImageElement).src = require("@/assets/images/noimage1.png"); 
      }
    };
    // 画像読み込みエラー発生
    const onFoodImageError = (event: Event) =>{
      NativeClient.sendDebugLog("CommonHorizontalRecipeListItem onImageError");
      if (event.target) {
        NativeClient.sendDebugLog("CommonHorizontalRecipeListItem onImageError target exist");
        (event.target as HTMLImageElement).src = require("@/assets/images/food_noimage.png"); 
      }
    };

    // 閲覧履歴のレシピの画像サイズ
    const historyRecipeImageSize = computed(() => {
      const screenWidth = window.innerWidth;
      const areaWidth = (screenWidth - 64) / 10;
      const imageWidth = (areaWidth - 5 * 0.8) / numberOfHistoryRecipeImages;
      NativeClient.sendDebugLog("MypageReports historyRecipeImageSize imageWidth = " + imageWidth);
      return {
        width: `${imageWidth}rem`,
        height: `${imageWidth}rem`
      }
    });

    // 食材使い切り（月間）画面へ
    const clickUsedFoods = () => {
      NativeClient.sendDebugLog("MypageReports clickUsedFoods");

      // TODO: 食材使い切り（月間）は、2023/8リリース予定
      // // 表示月初期化
      // store.dispatch("mypage/updateSelectedMonthIndex", null);
      //
      // router.push(`/mypage-report`);
    };

    // 閲覧履歴へ
    const clickHistoryRecipe = () => {
      NativeClient.sendDebugLog("MypageReports clickHistoryRecipe");
      router.push(`/mypage-history`);
    };

    return {
      isValidInquiry,
      isValidUsedFoods,
      isValidHistoryRecipe,
      isExistUsedFoodList,
      isNoRankingPreviousMonth,
      isNoHistory,
      inquiryDescriptionText,
      usedFoodsTitleText,
      noExistUsedFoodListText,
      userRankingtitleText,
      noUserRankingText,
      preRankingValueText,
      rankingValueText,
      postRankingValueText,
      numberOfUsedFoodsText,
      historyRecipeTitleText,
      noHistoryMessageText,
      historyRecipeDescriptionText,
      displayUsedFoodList,
      recipeImageList,
      usedFoodListItemImageSize,
      historyRecipeImageSize,
      onImageError,
      onFoodImageError,
      clickUsedFoods,
      clickHistoryRecipe
    };
  }
});
